// @flow
import * as React from 'react';
import { Box, ButtonBase, Stack, styled, SxProps, Typography, TypographyProps } from '@mui/material';
import { MedicareFormFields } from '.';
import { enqueueSnackbar } from 'notistack';
import { Copy, Plus } from '@phosphor-icons/react';
import { replaceAll } from '@coverright/utils';

type Props = {
  values: MedicareFormFields;
  imgPrefix?: string;
  sx?: SxProps;
  copyButton?: boolean;
};

export function MedicareCardView({ values, imgPrefix = '', sx = {}, copyButton }: Props) {
  return (
    <Box
      data-test={'medicare-card-view'}
      sx={{
        background:
          'linear-gradient(180deg, rgba(9,48,135,1) 10%, rgba(255,255,255,1) 10%)',
        borderBottom: '16px solid #d3203a',
        borderTop: '1px solid #093087',
        borderRadius: '19px',
        maxWidth: 416,
        ...sx
      }}
    >
      <Box
        component={'img'}
        src={imgPrefix + '/assets/img/medicare-card-background.png'}
        sx={{ height: 'auto', width: '100%' }}
      />

      <Box sx={{ pb: 1, px: 2 }}>
        <Label>Name</Label>
        <Stack sx={{ mb: 0.3 }} gap={2} direction={'row'} alignItems={'flex-start'}>
          <Value>{values.eligibleName}</Value>
          {copyButton && values.eligibleName && values.eligibleName !== '_____ __________' && <ButtonBase sx={{ mt: .2 }} onClick={() => {
            navigator.clipboard.writeText(values.eligibleName!).then(() => enqueueSnackbar('Copied!', { variant: 'info' }));
          }}>
            <Copy size={18} weight={'bold'} color="#a6a6a6" />
          </ButtonBase>}
        </Stack>

        <Label>Medicare Number</Label>
        <Stack sx={{ mb: 0.3 }} gap={2} direction={'row'} alignItems={'flex-start'}>
          <Value>{values.medicareNumber}</Value>
          {copyButton && values.medicareNumber && values.medicareNumber !== '____-___-____' && <ButtonBase sx={{ mt: .2 }} onClick={() => {
            navigator.clipboard.writeText(replaceAll(values.medicareNumber!, '-', '')).then(() => enqueueSnackbar('Copied!', { variant: 'info' }));
          }}>
            <Copy size={18} weight={'bold'} color="#a6a6a6" />
          </ButtonBase>}
        </Stack>

        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 } }}>
          <div>
            <Label>Entitled to</Label>
            <Value>Hospital (part A)</Value>
            <Value>medical (part b)</Value>
          </div>
          <div>
            <Label>Coverage starts</Label>
            <Stack gap={2} direction={'row'} alignItems={'flex-start'}>
              <Value>{values.partADate}</Value>
              {copyButton && values.partADate && values.partADate !== '__/__/____' && <ButtonBase sx={{ mt: .2 }} onClick={() => {
                navigator.clipboard.writeText(values.partADate!).then(() => enqueueSnackbar('Copied!', { variant: 'info' }));
              }}>
                <Copy size={18} weight={'bold'} color="#a6a6a6" />
              </ButtonBase>}
            </Stack>
            <Stack gap={2} direction={'row'} alignItems={'flex-start'}>
              <Value>{values.partBDate}</Value>
              {copyButton && values.partBDate && values.partBDate !== '__/__/____' && <ButtonBase sx={{ mt: .2 }} onClick={() => {
                navigator.clipboard.writeText(values.partBDate!).then(() => enqueueSnackbar('Copied!', { variant: 'info' }));
              }}>
                <Copy size={18} weight={'bold'} color="#a6a6a6" />
              </ButtonBase>}
            </Stack>
          </div>
        </Box>
      </Box>
    </Box>
  );
}

const Label = styled((props: TypographyProps) => (
  <Typography
    {...props}
    className={props.className + ' medicare-card-label'}
  />
))({
  fontSize: 11,
  color: '#082E85',
  fontWeight: 600,
  lineHeight: '16px',
  textAlign: 'left',
});

const Value = styled((props: TypographyProps) => (
  <Typography
    {...props}
    className={props.className + ' medicare-card-value'}
  />
))(({ theme }) => ({
  fontSize: 20,
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
  },
  color: '#000',
  fontWeight: 500,
  lineHeight: '27px',
  textTransform: 'uppercase',
  textAlign: 'left',
}));
